module CodeEntry

open Fable.React
open Fable.React.Props
open Illy.SafeStack.Client
open Fable.Core.JsInterop

type CustomSettings = 
    {
        IndividualFieldCSSClasses: string option
    }

module CustomSettings = 
    let defaults = 
        {
            IndividualFieldCSSClasses = None
        }

type Control = 
    {
        Id: string
        Values: string[]
        AllowSubmit: bool
        Error: string option
        Warning: string option
        Size: int
        Enabled: bool
    }

type Model = ControlState<Control, CustomSettings> 

type Msg =
    | ValueEntered of int * string
    | CodePasted of string

type Event =
    | Submit of string
    | StartedEntry

let private initControl id size = 
    {
        Id = id
        Values = Array.init size (fun _ -> "")
        AllowSubmit = false
        Error = None
        Warning = None
        Size = size
        Enabled = true
    }

let init id size settings : Model = 
    {
        Control = initControl id size
        Settings = ControlSettings.defaultOptions
        CustomSettings = settings 
    }

let private updateControl control msg = 
    match msg with 
    | ValueEntered(idx, v) -> 
        if idx >= control.Size then 
            {control with 
                Error = Some (sprintf "Max length is restricted to %d." control.Size)
            }, SideEffect.none
        else
            control.Values.[idx] <- v

            if idx < control.Size - 1 && System.String.IsNullOrEmpty v = false then
                let ele = Browser.Dom.document.getElementById (control.Id + "_" + ((idx+1).ToString()))
                ele.focus()

            let allDigits = control.Values |> Array.forall (fun a -> System.String.IsNullOrWhiteSpace a = false)

            {control with 
                Error = None
                AllowSubmit = allDigits
            },
            if allDigits then
                (control.Values |> String.concat "" |> Event.Submit |> SideEffect.Event)
            else
                SideEffect.Event StartedEntry
    | CodePasted code ->
        if code.Length > control.Size then 
            { control with 
                Error = Some (sprintf "Max length is restricted to %d." control.Size)
            }, SideEffect.none
        else
            {
                control with
                   Values = code.ToCharArray() |> Array.map (System.Char.ToString)
            }, Event.Submit code |> SideEffect.Event

let update msg (model: Model) =
    let m, c = updateControl model.Control msg
    { model with Control = m }, c

let render loading (model: Model) dispatch =
    section
        [
            ClassName ("code-entry" + match model.Settings.CSSClasses with [] -> "" | cls -> " " + (cls |> String.concat " "))
            OnPaste (fun clip -> 
                let content = clip.clipboardData.getData("text/plain")
                if System.String.IsNullOrWhiteSpace content = false then
                    dispatch (CodePasted content)
            ) 
        ]
        [
            let clsName = match model.CustomSettings.IndividualFieldCSSClasses with None -> "" | Some cls -> " " + cls
            for idx in [0.. model.Control.Values.Length - 1] do
                let v = model.Control.Values.[idx]
                yield input [
                    Id (model.Control.Id + "_" + idx.ToString())
                    ClassName (jQueryUI.addSkeletonScreen clsName None loading)
                    Type "text"
                    OnChange (fun ev -> if not loading then dispatch (ValueEntered(idx, !!ev.target?value)))
                    OnKeyDown (fun ev ->
                        if ev.code = "Backspace" && v = "" && idx > 0 then
                            let ele = Browser.Dom.document.getElementById(model.Control.Id + "_" + (idx-1).ToString())
                            ele.focus()
                    )
                    MaxLength 1
                    Value v
                    Disabled (loading || model.Control.Enabled = false)
                ]
        ]
    
// Utility functions

let resetText (model: Model) =
    let ele = Browser.Dom.document.getElementById(model.Control.Id + "_0")
    ele.focus()

    {model with 
        Control = 
            {model.Control with 
                Error = None 
                Warning = None
                Values = Array.init model.Control.Size (fun _ -> "")
            }
    }

let resetValidations (model: Model) = 
    {model with 
        Control = 
            {model.Control with 
                Error = None 
                Warning = None
            }
    }

let setValidations (model: Model) errors warnings = 
    {model with 
        Control = 
            {model.Control with 
                Error = errors 
                Warning = warnings
            }
    }

let toString (model: Model) =
     model.Control.Values |> String.concat ""
